import PropTypes from 'prop-types';

import {Group, Line as FabricLine, Triangle as FabricTriangle} from 'fabric';

import {limitReached} from '../canvas-helper';
import {ArrowLayoutManager} from '../useCustomControls';

const Line = (props) => {
    const {canvas, stroke, width, dashed} = props;

    let isDrawing = false;
    let line = null;
    let arrow = null;

    canvas.on('mouse:down', (o) => {
        if (limitReached(canvas)) {
            return;
        }

        // don't draw if an object is selected on the canvas
        if (canvas.getActiveObject()) {
            return;
        }

        isDrawing = true;

        let pointer = canvas.getScenePoint(o.e);
        let points = [pointer.x, pointer.y, pointer.x, pointer.y];
        line = new FabricLine(points, {
            strokeWidth: width,
            fill: 'black',
            strokeDashArray: dashed ? [15, 15] : null,
            stroke: stroke,
            originX: 'center',
            originY: 'center',
            selectable: false,
            hasBorders: false,
            hasControls: false,
        });

        canvas.add(line);
    });

    canvas.on('mouse:move', (o) => {
        if (!isDrawing) {
            return;
        }

        const pointer = canvas.getScenePoint(o.e);

        const left = pointer.x;
        const top = pointer.y;

        line.set({x2: left, y2: top});

        const angle = calcArrowAngle(line.get('x1'), line.get('y1'), left, top) + 90;

        if (arrow === null) {
            arrow = new FabricTriangle({
                left: left,
                top: top,
                originX: 'center',
                originY: 'center',
                hasBorders: false,
                hasControls: false,
                lockScalingX: true,
                lockScalingY: true,
                lockRotation: true,
                angle: angle,
                width: 30,
                height: 30,
                fill: stroke,
                stroke: stroke,
            });
            canvas.add(arrow);
        } else {
            arrow.angle = angle;
            arrow.left = left;
            arrow.top = top;
        }

        canvas.renderAll();
    });

    canvas.on('mouse:up', () => {
        isDrawing = false;

        if (line && arrow) {
            const x1 = line.get('x1');
            const y1 = line.get('y1');
            const x2 = line.get('x2');
            const y2 = line.get('y2');
            const dist = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
            canvas.remove(line);
            canvas.remove(arrow);
            if (dist < 10) {
                line = null;
                arrow = null;
                canvas.renderAll();
                return;
            }

            const group = new Group([line, arrow], {
                custom: {
                    type: 'line',
                    x1, y1, x2, y2,
                    stroke,
                    width,
                    dashed: !!dashed,
                },
                objectCaching: false,
                hasBorders: false,
                hasControls: false,
            });
            group.layoutManager = new ArrowLayoutManager();
            canvas.add(group);
            canvas.sendObjectToBack(group);
            canvas.renderAll();
            line = null;
            arrow = null;
        }
    });

};

Line.propTypes = {
    canvas: PropTypes.object.isRequired,
    stroke: PropTypes.string,
    width: PropTypes.number
};

export default Line;

export function calcArrowAngle(x1, y1, x2, y2) {
    let angle, x, y;

    x = (x2 - x1);
    y = (y2 - y1);

    if (x === 0) {
        angle = (y === 0) ? 0 : (y > 0) ? Math.PI / 2 : Math.PI * 3 / 2;
    } else if (y === 0) {
        angle = (x > 0) ? 0 : Math.PI;
    } else {
        angle = (x < 0) ? Math.atan(y / x) + Math.PI : (y < 0) ? Math.atan(y / x) + (2 * Math.PI) : Math.atan(y / x);
    }

    return (angle * 180 / Math.PI);
}

export function buildArrowChildren(x1, y1, x2, y2, stroke, width, dashed) {
    const line = new FabricLine([x1, y1, x2, y2], {
        strokeWidth: width,
        fill: 'black',
        strokeDashArray: dashed ? [15, 15] : null,
        stroke,
        originX: 'center',
        originY: 'center',
        selectable: false,
        hasBorders: false,
        hasControls: false,
    });
    const triangle = new FabricTriangle({
        left: x2,
        top: y2,
        originX: 'center',
        originY: 'center',
        hasBorders: false,
        hasControls: false,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
        angle: calcArrowAngle(x1, y1, x2, y2) + 90,
        width: 30,
        height: 30,
        fill: stroke,
        stroke,
    });
    return {line, triangle};
}
